/* eslint-disable max-len, object-curly-newline, react/jsx-one-expression-per-line */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Search from '@mui/icons-material/Search';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ContentCopy from '@mui/icons-material/ContentCopy';
import CustomTable from '../../components/table/CustomTable';
import { NotificationContext } from '../../contexts/Notification';
import { UserContext } from '../../contexts/User';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { BaseUrl } from '../../config';

/*
 * Settings → MCP connector.
 *
 * Two independent ways to reach the connector, matching server/mcp/access.js:
 * an account-type list, and individually-assigned members. A member connects if
 * either applies, so this screen never implies that assigning someone removes
 * anyone else. Owners only — the tab is not rendered for lower account types.
 */

// The endpoint an MCP client is pointed at; BaseUrl already carries the /api/ suffix.
const CONNECTOR_URL = `${BaseUrl}mcp`;

const TABLE_HEADERS = [
  { label: 'Member', colSpan: 2 },
  { label: 'Account type', colSpan: 1 },
  { label: 'Allowed by role', colSpan: 1 },
  { label: 'Assign directly', colSpan: 1 },
];

const initials = (name) => {
  const letters = (name || '?').split(' ').map((part) => part[0]).slice(0, 2);
  return letters.join('').toUpperCase();
};

/** Three states, so a member on a custom role is not reported as a flat "No". */
const roleColumnLabel = (byRole) => {
  if (byRole === null) return 'By role level';
  return byRole ? 'Yes' : 'No';
};

function McpConnector() {
  const { displayNotification } = useContext(NotificationContext);
  const { activeWorkspace } = useContext(UserContext);
  const workspaceId = activeWorkspace?.id;

  const [enabled, setEnabled] = useState(true);
  const [allowedAccountTypes, setAllowedAccountTypes] = useState([]);
  const [selectableAccountTypes, setSelectableAccountTypes] = useState([]);
  const [assignedMemberIds, setAssignedMemberIds] = useState([]);
  const [members, setMembers] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // What the server last confirmed, so Cancel has something true to go back to.
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    if (!workspaceId) return undefined;
    let active = true;
    (async () => {
      setLoading(true);
      const res = await sendRequest({ method: 'get', url: API.MCP_ACCESS.get(workspaceId) });
      if (!active) return;
      if (res.success) {
        const next = {
          enabled: !!res.data.enabled,
          allowedAccountTypes: res.data.allowedAccountTypes || [],
          assignedMemberIds: res.data.assignedMemberIds || [],
        };
        setEnabled(next.enabled);
        setAllowedAccountTypes(next.allowedAccountTypes);
        setAssignedMemberIds(next.assignedMemberIds);
        setSelectableAccountTypes(res.data.selectableAccountTypes || []);
        setMembers(res.data.members || []);
        setConfigured(!!res.data.configured);
        setSaved(next);
      } else {
        displayNotification({ message: res.data?.error || 'Failed to load MCP connector settings', severity: 'error' });
      }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [workspaceId]);

  const dirty = useMemo(() => {
    if (!saved) return false;
    const sameTypes = saved.allowedAccountTypes.length === allowedAccountTypes.length
      && saved.allowedAccountTypes.every((type) => allowedAccountTypes.includes(type));
    const sameMembers = saved.assignedMemberIds.length === assignedMemberIds.length
      && saved.assignedMemberIds.every((id) => assignedMemberIds.includes(id));
    return saved.enabled !== enabled || !sameTypes || !sameMembers;
  }, [saved, enabled, allowedAccountTypes, assignedMemberIds]);

  const toggleAccountType = (accountType) => {
    setAllowedAccountTypes((prev) => (prev.includes(accountType)
      ? prev.filter((type) => type !== accountType)
      : [...prev, accountType]));
  };

  const toggleAssigned = (memberId) => {
    setAssignedMemberIds((prev) => (prev.includes(memberId)
      ? prev.filter((id) => id !== memberId)
      : [...prev, memberId]));
  };

  const revert = () => {
    if (!saved) return;
    setEnabled(saved.enabled);
    setAllowedAccountTypes(saved.allowedAccountTypes);
    setAssignedMemberIds(saved.assignedMemberIds);
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    const res = await sendRequest({
      method: 'put',
      url: API.MCP_ACCESS.update(workspaceId),
      data: { enabled, allowedAccountTypes, assignedMemberIds },
    });
    setSaving(false);

    if (!res.success) {
      displayNotification({ message: res.data?.error || 'Could not save MCP connector access', severity: 'error' });
      return;
    }

    // Re-seed from the response, not from local state: the server drops members
    // who were deactivated while this screen was open.
    const next = {
      enabled: !!res.data.enabled,
      allowedAccountTypes: res.data.allowedAccountTypes || [],
      assignedMemberIds: res.data.assignedMemberIds || [],
    };
    setEnabled(next.enabled);
    setAllowedAccountTypes(next.allowedAccountTypes);
    setAssignedMemberIds(next.assignedMemberIds);
    setSaved(next);
    setConfigured(true);

    const ignored = res.data.ignoredMemberIds || [];
    displayNotification({
      message: ignored.length
        ? `Saved. ${ignored.length} selected member(s) were skipped because they are no longer active in this workspace.`
        : 'MCP connector access updated',
      severity: ignored.length ? 'info' : 'success',
    });
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(CONNECTOR_URL);
      displayNotification({ message: 'Connector URL copied', severity: 'success' });
    } catch (error) {
      displayNotification({ message: 'Could not copy — select the URL and copy it manually', severity: 'info' });
    }
  };

  /*
   * Whether a member would get in on role alone, mirroring
   * constants/mcpAccess.js isAllowedByRole. Shown read-only so an owner can see
   * why someone already has access before assigning them redundantly. Members on
   * a custom role are marked rather than resolved: their level is not in this
   * payload, and guessing it here would contradict the server.
   */
  const allowedByRole = (member) => {
    if (member.hasCustomRole) return null;
    return allowedAccountTypes.includes(member.accountType);
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return members;
    return members.filter((member) => (member.name || '').toLowerCase().includes(query)
      || (member.email || '').toLowerCase().includes(query));
  }, [members, search]);

  const roleAllowedCount = members.filter((member) => allowedByRole(member) === true).length;

  const items = filtered.map((member) => {
    const byRole = allowedByRole(member);
    return {
      member: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: '50%', background: 'var(--blue-7, #D8E6FC)', color: 'var(--primary, #213D69)', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
            {initials(member.name)}
          </span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' }}>{member.name}</span>
            <span style={{ display: 'block', fontSize: 12, color: 'var(--dark-3, #546077)' }}>{member.email}</span>
          </span>
        </div>
      ),
      accountType: (
        <span style={{ fontSize: 14, color: 'var(--dark-2, #293855)' }}>
          {member.accountType}
          {member.hasCustomRole && <span style={{ fontSize: 12, color: 'var(--dark-4, #7D8698)', display: 'block' }}>custom role</span>}
        </span>
      ),
      allowedByRole: (
        <span style={{ fontSize: 13, fontWeight: 600, color: byRole ? 'var(--green-1, #256D23)' : 'var(--dark-4, #7D8698)' }}>
          {roleColumnLabel(byRole)}
        </span>
      ),
      assignDirectly: (
        <Checkbox
          checked={assignedMemberIds.includes(member.memberId)}
          disabled={!enabled}
          onChange={() => toggleAssigned(member.memberId)}
          color="primary"
        />
      ),
    };
  });

  return (
    <div style={{ padding: '20px 4px', fontFamily: 'Inter, sans-serif' }}>
      {/* Master switch + endpoint */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '16px 18px', border: '1px solid var(--dark-6, #E0E1E6)', borderRadius: 10, marginBottom: 22, background: enabled ? '#fff' : 'var(--dark-10, #FAFAFA)' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' }}>MCP connector</span>
            <span style={{ fontSize: 12, fontWeight: 600, borderRadius: 20, padding: '2px 8px', ...(enabled ? { background: '#E7F6E6', color: 'var(--green-1, #256D23)' } : { background: 'var(--dark-7, #ECECF0)', color: 'var(--dark-3, #546077)' }) }}>
              {enabled ? 'On' : 'Off'}
            </span>
          </div>
          <p style={{ fontSize: 14, color: 'var(--dark-3, #546077)', margin: '4px 0 0', maxWidth: 640 }}>
            Lets approved people read this workspace&apos;s activity data from an AI client such as Claude or ChatGPT. Read-only — nothing can be changed through it. When off, nobody can connect, including directly-assigned members.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <code style={{ fontSize: 13, background: 'var(--dark-9, #FBFBFC)', border: '1px solid var(--dark-6, #E0E1E6)', borderRadius: 6, padding: '4px 8px', color: 'var(--dark-2, #293855)' }}>{CONNECTOR_URL}</code>
            <Button size="small" startIcon={<ContentCopy style={{ fontSize: 15 }} />} onClick={copyUrl} sx={{ textTransform: 'none', color: 'var(--primary, #213D69)' }}>Copy</Button>
          </div>
        </div>
        <Switch checked={enabled} disabled={loading} onChange={() => setEnabled((prev) => !prev)} color="primary" />
      </div>

      {/* Allowed account types */}
      <div style={{ marginBottom: 26 }}>
        <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--dark-1, #0E1E40)', margin: 0 }}>Account types allowed to connect</h4>
        <p style={{ fontSize: 14, color: 'var(--dark-3, #546077)', margin: '4px 0 12px', maxWidth: 640 }}>
          Anyone with a ticked account type can connect. Members on a custom role are compared against the lowest type you tick.
          {!configured && ' This workspace is on the default — owners only — until you save a change.'}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {selectableAccountTypes.map((accountType) => (
            <label
              key={accountType}
              htmlFor={`mcp-type-${accountType}`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 12px 4px 4px', border: '1px solid var(--dark-6, #E0E1E6)', borderRadius: 8, cursor: enabled ? 'pointer' : 'default', opacity: enabled ? 1 : 0.6, background: allowedAccountTypes.includes(accountType) ? 'var(--blue-7, #D8E6FC)' : '#fff' }}
            >
              <Checkbox
                id={`mcp-type-${accountType}`}
                size="small"
                checked={allowedAccountTypes.includes(accountType)}
                disabled={!enabled}
                onChange={() => toggleAccountType(accountType)}
                color="primary"
              />
              <span style={{ fontSize: 14, color: 'var(--dark-1, #0E1E40)' }}>{accountType}</span>
            </label>
          ))}
        </div>
        {enabled && allowedAccountTypes.length === 0 && (
          <p style={{ fontSize: 13, color: 'var(--dark-3, #546077)', margin: '10px 0 0' }}>
            No account types ticked — only the members you assign below will be able to connect.
          </p>
        )}
      </div>

      {/* Individual assignment */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div>
          <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--dark-1, #0E1E40)', margin: 0 }}>Assign individual members</h4>
          <p style={{ fontSize: 14, color: 'var(--dark-3, #546077)', margin: '4px 0 0', maxWidth: 560 }}>
            Give one person the connector without changing their account type. This adds access — it never removes it, so unticking someone who is allowed by role changes nothing.
          </p>
        </div>
        <TextField
          size="small"
          placeholder="Search members"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{ startAdornment: (<InputAdornment position="start"><Search style={{ fontSize: 18, color: 'var(--dark-4, #7D8698)' }} /></InputAdornment>) }}
          sx={{ width: 240 }}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', borderRadius: 8, marginBottom: 8, background: 'var(--dark-9, #FBFBFC)', border: '1px solid var(--dark-6, #E0E1E6)' }}>
        <InfoOutlined style={{ fontSize: 18, color: 'var(--primary, #213D69)', flexShrink: 0, marginTop: 1 }} />
        <p style={{ fontSize: 14, color: 'var(--dark-2, #293855)', margin: 0 }}>
          A connected member reads the whole workspace, not just their own team — the same figures the Reports screens show.
          <span style={{ color: 'var(--dark-3, #546077)' }}>{` ${roleAllowedCount} allowed by account type · ${assignedMemberIds.length} assigned individually`}</span>
        </p>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}><CircularProgress size={26} /></div>
      ) : (
        <CustomTable headers={TABLE_HEADERS} items={items} noItemText="No members match your search." />
      )}

      {/* Save bar — the two halves are saved together, so there is one action. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 22 }}>
        <Button
          variant="contained"
          disabled={!dirty || saving || loading}
          onClick={save}
          sx={{ textTransform: 'none', backgroundColor: 'var(--primary, #213D69)', '&:hover': { backgroundColor: '#1a3156' } }}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
        <Button disabled={!dirty || saving} onClick={revert} sx={{ textTransform: 'none', color: 'var(--dark-3, #546077)' }}>Cancel</Button>
        {dirty && <span style={{ fontSize: 13, color: 'var(--dark-4, #7D8698)' }}>Unsaved changes</span>}
      </div>
    </div>
  );
}

export default McpConnector;
